// ---------------------------------------------------------------------------
// Block Definition: text_block
//
// Block of text slots with optional card-style layout.  Supports slot-level
// styling (italic, bold, serif, center), markdown, max_lines, and card
// background/accent configuration.
//
// LLM-only extraction — no structured or deterministic phases.
// ---------------------------------------------------------------------------

const EVIDENCE_DESC = "Verbatim sentence(s) from the SOURCE that justify this value — quote exactly, do not paraphrase. If combining separate spans, join them with ' … '. null if not directly stated.";

function nullable(type, description, extra = {}) {
  const t = Array.isArray(type) ? type : [type, "null"];
  return { type: t, ...(description ? { description } : {}), ...extra };
}

/** Dot-path lookup (object keys and array indices both work — array access
 *  is just string-keyed property access in JS). Returns undefined if any
 *  segment along the way is missing, rather than throwing. */
function getByPath(root, path) {
  if (root == null || !path) return undefined;
  let cur = root;
  for (const segment of String(path).split(".")) {
    if (cur == null) return undefined;
    cur = cur[segment];
  }
  return cur;
}

/** True when the block has no role:"llm_gen" slots and at least one
 *  role:"extract" slot — role:"literal" slots are ignored since they never
 *  need crystallizer resolution (the renderer fills them directly). Such a
 *  block resolves entirely from structured pin data, no LLM call. */
function isAllExtract(block) {
  const relevant = (block.slots || []).filter((s) => s.role === "extract" || s.role === "llm_gen");
  return relevant.length > 0 && relevant.every((s) => s.role === "extract");
}

export const textBlockDef = {
  type: "text_block",
  label: "Text Block",
  description: "Block of styled text slots with optional card layout.",

  crystallizer: {
    // A block whose slots are ALL role:"extract" resolves entirely from
    // structured pin data in Phase 1 (extractStructured below) — no LLM
    // call. Any block with even one role:"llm_gen" slot behaves exactly as
    // before (unchanged — this is the ["llm"] default every existing
    // template already gets).
    extractionPhases(block) {
      return isAllExtract(block) ? ["structured"] : ["llm"];
    },

    // Phase 1 (viz_dsl_crystallizer.js) — called only when extractionPhases
    // includes "structured". `structured._raw` is the source pin's parsed
    // JSON, untouched. Each extract slot's `extract_key` (or `key` if
    // extract_key is absent) is a dot-path into that JSON — e.g.
    // "phases.0.name". Values are expected to already be display-ready
    // strings/numbers (formatting composite values like a fee range or a
    // week span is the caller's job when building the pin, not this hook's)
    // so this stays a plain lookup, not a formatter.
    extractStructured(block, structured) {
      const raw = structured && structured._raw;
      if (raw == null || typeof raw !== "object") return null;
      const result = {};
      let any = false;
      for (const s of (block.slots || [])) {
        if (s.role !== "extract" || !s.key) continue;
        const val = getByPath(raw, s.extract_key || s.key);
        result[s.key] = val ?? null;
        if (val != null) any = true;
      }
      return any ? result : null;
    },

    buildDataSchema(block, opts) {
      const hints = opts.hints || [];
      const ownedKeys = new Set(hints.map(h => h.key).filter(Boolean));
      const props = {};
      for (const s of (block.slots || [])) {
        if (s.role === "llm_gen" && s.key) {
          if (ownedKeys.size > 0 && !ownedKeys.has(s.key)) continue;
          const desc = [s.prompt, s.max_words ? `Max ${s.max_words} words.` : null]
            .filter(Boolean).join(" ");
          props[s.key] = nullable("string", desc);
          if (s.cite) props[s.key + "__evidence"] = nullable("string", EVIDENCE_DESC);
        }
      }
      return { type: "object", properties: props, required: Object.keys(props), additionalProperties: false };
    },

    buildShapeDescription(block) {
      const getSlotKeys = (node) => {
        const keys = [];
        function walk(n) {
          if (!n || typeof n !== "object") return;
          if (Array.isArray(n)) { n.forEach(walk); return; }
          if ((n.role === "llm_gen" || n.role === "extract") && n.key) {
            keys.push({ key: n.key });
          }
          if (n.slots !== undefined) walk(n.slots);
        }
        walk(node);
        return keys;
      };
      const k = getSlotKeys(block);
      return `{ ${k.map(x => `"${x.key}": "<string>"`).join(", ")} }`;
    },

    isBlockLevelTarget() { return false; },
    // All-extract blocks never contribute to an LLM response_format — their
    // slots are excluded from buildDataSchema's properties anyway (it only
    // emits role:"llm_gen" keys), so this just avoids a wasted null-schema
    // inclusion once extraction has already resolved them in Phase 1.
    skipFromPinSchema(block) { return isAllExtract(block); },
    isCodeDerived() { return false; },
    collectHints() { return []; },
    buildPromptSupplement() { return ""; },
    postProcess(data) { return data; },
  },

  renderer: null, // wired by ui/src/blockRegistryInit.js

  editor: {
    icon: "text",
    category: "layout",
    defaultTemplate: {
      type: "text_block",
      slots: [
        { role: "llm_gen", key: "takeaway", max_words: 8, style: "italic" },
        { role: "llm_gen", key: "evidence", max_words: 50 },
      ],
    },
    configFields: [
      { name: "slot_layout", type: "select", label: "Layout", options: ["simple", "cards"], defaultValue: "simple" },
      { name: "label", type: "string", label: "Block label" },
      { name: "background", type: "color", label: "Background color" },
      { name: "card_background", type: "color", label: "Card background (cards)" },
      { name: "max_lines", type: "number", label: "Max lines per slot" },
      { name: "min_font_scale", type: "number", label: "Min font scale (0-1)", min: 0, max: 1, step: 0.05 },
      { name: "proportion", type: "number", label: "Proportion weight", step: 0.1 },
      { name: "accent_bar", type: "object", label: "Accent bar",
        fields: [
          { name: "color", type: "color", label: "Accent color" },
          { name: "width", type: "number", label: "Width px", defaultValue: 3 },
        ]
      },
      { name: "slots", type: "array", label: "Slots",
        itemSchema: { fields: [
          { name: "role", type: "select", label: "Role", options: ["llm_gen", "extract", "literal"], defaultValue: "llm_gen" },
          { name: "key", type: "string", label: "Key" },
          { name: "max_words", type: "number", label: "Max words" },
          { name: "style", type: "select", label: "Style", options: ["normal", "italic", "bold", "italic_serif", "italic_serif_center", "small"], defaultValue: "normal" },
          { name: "format", type: "select", label: "Value format", options: ["", "currency", "currency_m", "pct_1dp", "num_1dp", "num_0dp"], defaultValue: "" },
          { name: "label", type: "string", label: "Prefix label" },
          { name: "prompt", type: "string", label: "LLM prompt" },
        ]}
      },
    ],
  },
};
